/** Where a held message's actions go (see components/ActionSheet): the message stays put, its actions come to it. */

export interface AnchorRect {
  top: number
  bottom: number
  left: number
  right: number
}

export interface AnchoredInput {
  /** The bubble being held, on screen. */
  rect: AnchorRect
  /** The panel about to be placed: its size, and where the copy of the message sits inside it. */
  panel: { width: number; height: number; copyTop: number; copyHeight: number }
  viewport: { width: number; height: number }
  /** Your own messages hang from their right edge, everyone else's from their left. */
  side: 'own' | 'other'
  /** The strips a phone keeps for the clock and the home indicator. A screen with neither leaves this out. */
  safe?: { top: number; bottom: number }
}

/** The gap kept between the lifted group and the edges of the screen. */
export const MARGIN = 12

/** Keeps `value` within `[min, max]`, and at `min` when there is no room at all (a group taller than the screen). */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, Math.max(min, max)))
}

export function anchoredPlacement({ rect, panel, viewport, side, safe }: AnchoredInput): { left: number; top: number } {
  const wanted = side === 'own' ? rect.right - panel.width : rect.left
  const top = safe?.top ?? 0
  const bottom = safe?.bottom ?? 0
  return {
    left: clamp(wanted, MARGIN, viewport.width - panel.width - MARGIN),
    // The copy sits over the message it came from, then the whole group moves to stay on screen — clear of the strips
    // a phone keeps for itself, since a swipe up from the bottom one is the Home gesture, not a tap on Delete.
    top: clamp(rect.top - panel.copyTop, top + MARGIN, viewport.height - bottom - panel.height - MARGIN),
  }
}
